import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FolderLock, FolderOpen, FileText, LogOut, Shield, AlertTriangle, Clock, Eye, MessageSquare, Send, ChevronDown, ChevronUp, User } from 'lucide-react';
import { dataRoomAPI } from '../../services/api';
import FilePreviewModal from './components/FilePreviewModal';

function formatSize(bytes) {
  if (!bytes) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0, n = bytes;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

const PERM_LABEL = {
  view_only: 'View Only',
  view_print: 'View + Print',
  view_download: 'View + Download',
  view_download_print: 'View + Download + Print',
  redacted: 'Redacted',
  no_access: 'No Access',
};

export default function ExternalVDRViewer() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [jwt, setJwt] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [context, setContext] = useState(null);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [activeTab, setActiveTab] = useState('documents');

  // Q&A state
  const [questions, setQuestions] = useState([]);
  const [qaLoading, setQaLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [showNewQuestion, setShowNewQuestion] = useState(false);
  const [newQ, setNewQ] = useState({ title: '', body: '', assigned_to: '' });
  const [submittingQ, setSubmittingQ] = useState(false);
  const [expandedQ, setExpandedQ] = useState(null);
  const [questionDetail, setQuestionDetail] = useState(null);

  useEffect(() => {
    const go = async () => {
      setLoading(true);
      setErr('');
      try {
        const v = await dataRoomAPI.externalVerify(token);
        const activeJwt = v.data.token;
        sessionStorage.setItem('vdr_ext_jwt', activeJwt);
        setJwt(activeJwt);
        const me = await dataRoomAPI.externalMe(activeJwt);
        setContext(me.data);
        const f = await dataRoomAPI.externalFolders(activeJwt);
        setFolders(f.data.data || []);
      } catch (e) {
        setErr(e.response?.data?.detail || 'Invalid or expired invitation link');
        sessionStorage.removeItem('vdr_ext_jwt');
      } finally {
        setLoading(false);
      }
    };
    go();
  }, [token]);

  const loadFiles = useCallback(async (folderId) => {
    if (!jwt) return;
    try {
      const r = await dataRoomAPI.externalFiles(jwt, folderId);
      setFiles(r.data.data || []);
    } catch { setFiles([]); }
  }, [jwt]);

  useEffect(() => {
    if (currentFolder) loadFiles(currentFolder.id);
    else setFiles([]);
  }, [currentFolder, loadFiles]);

  const loadQuestions = useCallback(async () => {
    if (!jwt) return;
    setQaLoading(true);
    try {
      const [qRes, mRes] = await Promise.all([
        dataRoomAPI.externalListQuestions(jwt),
        dataRoomAPI.externalGetAssignableMembers(jwt),
      ]);
      setQuestions(qRes.data.data || []);
      setMembers(mRes.data.members || []);
    } catch { }
    finally { setQaLoading(false); }
  }, [jwt]);

  useEffect(() => {
    if (activeTab === 'qa' && jwt) loadQuestions();
  }, [activeTab, jwt, loadQuestions]);

  const handleSubmitQuestion = async () => {
    if (!newQ.title.trim()) return;
    setSubmittingQ(true);
    try {
      await dataRoomAPI.externalCreateQuestion(jwt, {
        title: newQ.title.trim(),
        body: newQ.body.trim(),
        assigned_to: newQ.assigned_to || null,
      });
      setNewQ({ title: '', body: '', assigned_to: '' });
      setShowNewQuestion(false);
      loadQuestions();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to submit question');
    } finally {
      setSubmittingQ(false);
    }
  };

  const toggleQuestion = async (qId) => {
    if (expandedQ === qId) {
      setExpandedQ(null);
      setQuestionDetail(null);
      return;
    }
    setExpandedQ(qId);
    try {
      const r = await dataRoomAPI.externalGetQuestion(jwt, qId);
      setQuestionDetail(r.data);
    } catch { setQuestionDetail(null); }
  };

  const signOut = () => {
    sessionStorage.removeItem('vdr_ext_jwt');
    navigate('/login');
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center"><FolderLock size={40} className="mx-auto mb-4 text-blue-600 animate-pulse" /><p className="text-gray-600">Verifying access...</p></div>
    </div>
  );

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center max-w-md mx-auto p-8">
        <AlertTriangle size={40} className="mx-auto mb-4 text-red-500" />
        <h2 className="text-lg font-bold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600 text-sm">{err}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="external-vdr-viewer">
      {/* Top bar */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <FolderLock size={18} className="text-blue-600 shrink-0" />
            <div className="min-w-0">
              <h1 className="font-semibold text-gray-900 truncate" data-testid="ext-room-name">{context?.data_room?.name}</h1>
              <p className="text-xs text-gray-500 truncate">
                Welcome, <span className="font-medium">{context?.user?.name}</span> ({context?.user?.role})
              </p>
            </div>
          </div>
          <button onClick={signOut} className="inline-flex items-center px-3 py-1.5 border rounded-lg text-xs hover:bg-gray-50" data-testid="ext-signout">
            <LogOut size={12} className="mr-1" /> Sign out
          </button>
        </div>
      </header>

      {/* Watermark banner */}
      <div className="bg-amber-50 border-b border-amber-200 text-amber-800 text-xs px-5 py-2 flex items-center gap-2">
        <Shield size={13} />
        Your activity in this data room is audit-logged. Documents viewed or downloaded are watermarked with your email, IP address, and timestamp.
      </div>

      {/* Tab bar */}
      <div className="max-w-7xl mx-auto px-5 pt-4">
        <div className="flex gap-1 border-b">
          <button
            onClick={() => setActiveTab('documents')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === 'documents' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid="tab-documents"
          >
            <FolderOpen size={14} className="inline mr-1.5 -mt-0.5" /> Documents
          </button>
          <button
            onClick={() => setActiveTab('qa')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === 'qa' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid="tab-qa"
          >
            <MessageSquare size={14} className="inline mr-1.5 -mt-0.5" /> Q&A
          </button>
        </div>
      </div>

      {/* Documents tab */}
      {activeTab === 'documents' && (
        <main className="max-w-7xl mx-auto px-5 py-6 grid grid-cols-12 gap-5">
          <aside className="col-span-12 md:col-span-4 lg:col-span-3 bg-white border rounded-lg p-3" data-testid="ext-folders">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Folders</h2>
            {folders.length === 0 ? (
              <p className="text-xs text-gray-400 py-4 text-center">No accessible folders</p>
            ) : (
              <div className="space-y-0.5">
                {folders.map(f => (
                  <button
                    key={f.id}
                    onClick={() => setCurrentFolder(f)}
                    className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-sm text-left ${currentFolder?.id === f.id ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-50 text-gray-700'}`}
                    data-testid={`ext-folder-${f.id}`}
                  >
                    <span className="flex items-center gap-1.5 min-w-0">
                      <FolderOpen size={13} className="text-blue-500" />
                      <span className="truncate">{f.name}</span>
                    </span>
                    <span className="text-[10px] text-gray-400 ml-2">{PERM_LABEL[f.permission] || f.permission}</span>
                  </button>
                ))}
              </div>
            )}
          </aside>

          <section className="col-span-12 md:col-span-8 lg:col-span-9 bg-white border rounded-lg" data-testid="ext-files">
            <div className="px-4 py-3 border-b">
              <h2 className="font-semibold text-gray-900">{currentFolder ? currentFolder.name : 'Select a folder'}</h2>
              {currentFolder && (
                <p className="text-xs text-gray-500">Effective permission: <span className="font-medium">{PERM_LABEL[currentFolder.permission] || currentFolder.permission}</span></p>
              )}
            </div>
            <div className="p-4">
              {!currentFolder ? (
                <div className="py-16 text-center text-gray-400 text-sm">
                  <Eye size={32} className="mx-auto mb-2 text-gray-300" />
                  Choose a folder from the left to view documents
                </div>
              ) : files.length === 0 ? (
                <div className="py-12 text-center text-gray-400 text-sm">No files in this folder</div>
              ) : (
                <div className="space-y-2">
                  {files.map(f => {
                    const canOpen = f.effective_permission && f.effective_permission !== 'no_access';
                    return (
                      <button type="button" key={f.id} onClick={() => canOpen && setPreviewFile(f)} disabled={!canOpen}
                        className={`w-full text-left border rounded-lg p-3 flex items-center gap-3 ${canOpen ? 'hover:border-blue-400 hover:bg-blue-50/40 cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
                        data-testid={`ext-file-${f.id}`}>
                        <FileText size={18} className="text-blue-500 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-gray-900 truncate">{f.name}</div>
                          <div className="text-[11px] text-gray-500 mt-0.5">
                            <Clock size={10} className="inline mr-1" />
                            {new Date(f.uploaded_at).toLocaleDateString()} • {formatSize(f.size)} • v{f.version || 1}
                          </div>
                        </div>
                        <div className="text-[10px] px-2 py-0.5 rounded bg-gray-100 text-gray-600 shrink-0 uppercase">
                          {PERM_LABEL[f.effective_permission] || f.effective_permission}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </main>
      )}

      {/* Q&A tab */}
      {activeTab === 'qa' && (
        <main className="max-w-3xl mx-auto px-5 py-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Questions & Answers</h2>
            <button
              onClick={() => setShowNewQuestion(!showNewQuestion)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
              data-testid="ask-question-btn"
            >
              <MessageSquare size={14} className="mr-1.5" /> Ask a Question
            </button>
          </div>

          {/* New Question Form */}
          {showNewQuestion && (
            <div className="bg-white border rounded-lg p-5 mb-6" data-testid="new-question-form">
              <h3 className="font-medium text-gray-900 mb-3">New Question</h3>
              <div className="space-y-3">
                <input
                  type="text" value={newQ.title} onChange={e => setNewQ(p => ({ ...p, title: e.target.value }))}
                  placeholder="Question title *" className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="q-title-input"
                />
                <textarea
                  value={newQ.body} onChange={e => setNewQ(p => ({ ...p, body: e.target.value }))}
                  placeholder="Provide more details..." rows={3} className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
                  data-testid="q-body-input"
                />
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Tag a team member (optional)</label>
                  <select
                    value={newQ.assigned_to} onChange={e => setNewQ(p => ({ ...p, assigned_to: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="q-assign-select"
                  >
                    <option value="">-- Select member --</option>
                    {members.map(m => (
                      <option key={m.id} value={m.id}>{m.name} ({m.email})</option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => { setShowNewQuestion(false); setNewQ({ title: '', body: '', assigned_to: '' }); }}
                    className="px-3 py-1.5 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                  <button onClick={handleSubmitQuestion} disabled={!newQ.title.trim() || submittingQ}
                    className="inline-flex items-center px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                    data-testid="submit-question-btn">
                    <Send size={13} className="mr-1.5" /> {submittingQ ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Questions list */}
          {qaLoading ? (
            <div className="py-12 text-center text-gray-400 text-sm">Loading questions...</div>
          ) : questions.length === 0 ? (
            <div className="py-12 text-center bg-white border rounded-lg">
              <MessageSquare size={32} className="mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500 text-sm">No questions yet. Ask the first one!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {questions.map(q => {
                const isExpanded = expandedQ === q.id;
                const statusColor = q.status === 'answered' ? 'bg-green-100 text-green-700' : q.status === 'closed' ? 'bg-gray-100 text-gray-600' : 'bg-amber-100 text-amber-700';
                return (
                  <div key={q.id} className="bg-white border rounded-lg" data-testid={`question-${q.id}`}>
                    <button onClick={() => toggleQuestion(q.id)} className="w-full text-left px-4 py-3 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 truncate">{q.title}</span>
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${statusColor}`}>{q.status}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          By {q.author_name} • {new Date(q.created_at).toLocaleDateString()} • {q.answer_count || 0} {q.answer_count === 1 ? 'answer' : 'answers'}
                          {q.assigned_to_name && <span className="ml-1">• Tagged: {q.assigned_to_name}</span>}
                        </div>
                      </div>
                      {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                    </button>
                    {isExpanded && (
                      <div className="border-t px-4 py-3 bg-gray-50/50">
                        {q.body && <p className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">{q.body}</p>}
                        {questionDetail?.answers?.length > 0 ? (
                          <div className="space-y-3">
                            <p className="text-xs font-semibold text-gray-500 uppercase">Answers</p>
                            {questionDetail.answers.map(a => (
                              <div key={a.id} className="bg-white border rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-1.5">
                                  <User size={12} className="text-blue-500" />
                                  <span className="text-xs font-medium text-gray-800">{a.author_name || 'AMC Team'}</span>
                                  <span className="text-[10px] text-gray-400">{new Date(a.created_at).toLocaleString()}</span>
                                </div>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{a.body}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 italic">No answers yet — awaiting response from the team.</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </main>
      )}

      <FilePreviewModal
        open={!!previewFile}
        onClose={() => setPreviewFile(null)}
        dataRoomId={context?.data_room?.id}
        file={previewFile}
        canEdit={false}
        externalJwt={jwt}
      />
    </div>
  );
}
